/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, cssMap, jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';

import { type IconProps, sizes } from '@atlaskit/icon';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

interface WrapperProps {
	primaryColor?: string;
	secondaryColor?: string;
	size?: keyof typeof sizes;
}

interface ExtendedWrapperProps extends WrapperProps, React.HTMLAttributes<HTMLSpanElement> {
	role?: string;
	'aria-label'?: string;
}

const sizeStyles = cssMap({
	small: { height: '16px', width: '16px' },
	medium: { height: '24px', width: '24px' },
	large: { height: '32px', width: '32px' },
	xlarge: { height: '48px', width: '48px' },
});

const getSize = ({ size }: WrapperProps) =>
	size ? `height: ${sizes[size]}; width: ${sizes[size]};` : null;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const IconWrapper = styled.span<WrapperProps>`
	${getSize};
	color: ${(p) => p.primaryColor || 'currentColor'};
	display: inline-block;
	fill: ${(p) => p.secondaryColor || token('elevation.surface')};
	flex-shrink: 0;
	line-height: 1;

	> svg {
		${getSize};
		max-height: 100%;
		max-width: 100%;
		overflow: hidden;
		pointer-events: none;
		vertical-align: bottom;
	}
`;

const baseStyles = css({
	display: 'inline-block',
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& > svg': {
		maxHeight: '100%',
		maxWidth: '100%',
		overflow: 'hidden',
		pointerEvents: 'none',
		verticalAlign: 'bottom',
	},
});

const CompiledIconWrapper = ({
	primaryColor,
	secondaryColor,
	size = 'medium',
	...props
}: ExtendedWrapperProps) => {
	return (
		<span
			css={[baseStyles, sizeStyles[size]]}
			style={{
				color: primaryColor || 'currentColor',
				fill: secondaryColor || token('elevation.surface'),
			}}
			{...props}
		/>
	);
};

export const CustomIcon = (props: IconProps) => {
	const isCompiledEnabled = fg('ux-3487-replace-styled-component-with-compiled');

	const { glyph: Glyph, primaryColor, secondaryColor, size, testId, label } = props;
	const glyphProps = { children: Glyph ? <Glyph role="presentation" /> : null };

	return isCompiledEnabled ? (
		<CompiledIconWrapper
			primaryColor={primaryColor}
			secondaryColor={secondaryColor}
			size={size}
			data-testid={testId}
			role={label ? 'img' : 'presentation'}
			aria-label={label || undefined}
			{...glyphProps}
		/>
	) : (
		<IconWrapper
			primaryColor={primaryColor}
			secondaryColor={secondaryColor}
			size={size}
			data-testid={testId}
			role={label ? 'img' : 'presentation'}
			aria-label={label || undefined}
			{...glyphProps}
		/>
	);
};
